import React, { useRef, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'

import { ArrowUpRightIcon, BookOpenIcon, InfoIcon } from 'lucide-react'

import { Box, Stack, Tooltip, Typography, alpha } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { brandText, brandTextHover, dimNeutralText } from '@repo/ui'

import { glossaryHref } from 'docs/glossary'
import { useGlossaryTerm } from 'docs/GlossaryProvider'

import { TermOverlay, hasExpandedEntry } from '../TermOverlay'

/**
 * Rendered size of an info affordance, in px. THE single source.
 *
 * Exported because the glyph is not always this component's own child: a few call sites pass a
 * custom trigger (a stat label and its dot as ONE hover target, so the words are part of the
 * target rather than a 14px dot nobody aims at) and therefore draw the glyph themselves. Every
 * one of those re-typed a size, and they disagreed — measured on /settings/billing, which
 * rendered THREE sizes of info affordance at once: 14 here, 16 from `InfoBlock`, 18 from
 * `BillingOverview`'s local `InfoDot` and its `InfoItem` label.
 *
 * Matching the numbers by hand would have fixed that page until the next call site. Import this.
 */
export const INFO_GLYPH_SIZE = 14

/**
 * The info affordance itself, for the call sites that must draw their own (see above).
 *
 * A component and not just the constant, because size is not the only thing that was drifting:
 * the same sweep found `strokeWidth` at 1, 1.5 and the lucide default, and the glyph itself split
 * between `InfoIcon` and `CircleHelpIcon` — so two affordances on one card were a different
 * WEIGHT and a different SHAPE, not merely a different size. Colour stays a prop because it
 * legitimately varies with what the glyph sits next to.
 *
 * ⚠️ THE GLYPH IS `InfoIcon`, AND THE SPLIT NOTED ABOVE IS NOW RESOLVED — resolved the OTHER
 * way from the one this component originally picked (VRG-517). It was `CircleHelpIcon`, and
 * that was the minority shape. Counted across `apps/platform`, `apps/app` and `packages/ui`
 * after the change: 41 `<InfoIcon` render sites against 3 `<CircleHelpIcon`, and all three of
 * those are literal Help affordances — the sidebar Help popover, the reader-setup page, and
 * `apps/app`'s Profile Help row. None of them route through this component.
 *
 * The shape is not decoration, it is a promise about what happens next. A `?` asks "do you need
 * help", which in this app means one of those three Help surfaces. This component explains a
 * TERM or a FIGURE, in place, on hover: that is an `i`. Reported as "an odd circle" on the
 * Product lifespan card, and the reader was right — a "?" beside a metric offers assistance
 * nobody asked for instead of naming what the number is. The customer guides had already
 * settled it in prose without anyone noticing the code disagreed: `settings-general.md` tells
 * a reader to hover "the **(i)** beside Reporting currency", and two other guides say "info
 * icon". They now describe what renders.
 *
 * It also converges the analytics row with itself. `AnalyticsBlock` draws its own glyph on the
 * `tooltip`-only branch (`InfoIcon` at 16, see that file), so a `term` tile and a `tooltip` tile
 * side by side used to be two different SHAPES. They are now one shape at two sizes, 14 and 16,
 * which is the residue of that split and the only part still worth a sweep.
 */
export const InfoGlyph = ({ color }: { color?: string }) => (
  <InfoIcon size={INFO_GLYPH_SIZE} strokeWidth={1.5} color={color} />
)

/**
 * The "this affordance carries a live caveat" mark — a tinted disc BEHIND the existing
 * glyph, never a second glyph beside it.
 *
 * Exported because the mark is not always drawn by this component: a call site passing its
 * own trigger (`AnalyticsBlock`'s stat tiles keep `InfoIcon` at 16 so a marked card's glyph
 * still matches the unmarked cards in the same row) has to draw it itself, and a second
 * hand-rolled disc is how the two would drift apart.
 *
 * ⚠️ NEUTRAL, not `warning.main`, and that is the whole point of the treatment. The caveats
 * this marks are properties of the DATA — "these scans have no resolved country", "these
 * transfers predate completion timestamps" — and most of them never clear, so an amber mark
 * would be permanently on. A warning tone that is always lit stops being read as a warning
 * and takes the one that mattered down with it. The disc says "there is something attached
 * to this number"; the tooltip says what.
 *
 * The tone is `dimNeutralText`, NOT `text.secondary`: that token is `#a1a1aa` in both modes
 * and measures 2.46:1 on the light surface, i.e. a mark nobody can see. Same helper, same
 * alphas, as `MetricAssumptionMarker`'s settled state, which is the neighbouring precedent
 * for a neutral footnote disc on this page.
 */
export const caveatMarkSx = (theme: {
  /*
    ⚠️ NARROWED, NOT `Theme` — the same trap `dimNeutralText` documents, and it bites
    identically here. The repo resolves two `@mui/material@5.18.0` instances (one under
    react 18.3.1, one under 19.1.0), so a `Theme` imported here and the `Theme` returned
    by a caller's `useTheme()` are structurally identical and nominally distinct. Typing
    the parameter as `Theme` cost 3 errors over baseline — a `TS2345 'Theme' is not
    assignable to 'Theme'` at each call site plus a `TS2859 Excessive complexity comparing
    types 'Theme' and 'Theme'` — measured, not guessed. Both instances satisfy the two
    fields actually read.
  */
  palette: { mode: string; text: { secondary: string } }
}) => {
  const tone = dimNeutralText(theme)
  return {
    width: 24,
    height: 24,
    borderRadius: '50%',
    justifyContent: 'center',
    color: tone,
    backgroundColor: alpha(tone, 0.14),
    border: `1px solid ${alpha(tone, 0.36)}`,
    '&:hover': { backgroundColor: alpha(tone, 0.24), color: tone },
  }
}

interface Props {
  /**
   * A glossary entry id. When given, the tooltip BODY is that term's `short` — read
   * from the glossary, never retyped here — and a footer links to its definition.
   *
   * Prefer this over `text` whenever the tooltip is explaining a term of art. Typing a
   * definition into a call site is how the tooltip and the glossary drift apart, and the
   * tooltip is where drift hurts most: it is read at the moment of confusion, by someone
   * with no way to tell it is stale (working-conventions §44).
   */
  term?: string
  /**
   * Suppress the definition/guide footer link.
   *
   * For PUBLIC surfaces — sign-in, sign-up — where the reader is not
   * authenticated. The footer points at `/docs/...`, which is behind the app;
   * offering an unauthenticated visitor a link out of a signup form is both a
   * dead end and an invitation to leave a flow they are halfway through.
   *
   * The BODY still comes from the glossary, so the wording stays single-sourced
   * (§44). Only the link is dropped.
   */
  hideDefinitionLink?: boolean
  /**
   * Literal body, for tooltips that explain a CONTROL rather than a term ("this filters
   * the list below"). If both are given, this overrides the glossary body but the footer
   * still points at the term — the case where a call site needs local phrasing but the
   * reader should still be able to reach the definition.
   */
  text?: React.ReactNode
  /**
   * A caveat on the thing being explained, rendered UNDER the body, below a rule, in the
   * secondary tone.
   *
   * Distinct from `text` because it is a different claim with a different lifetime. The body
   * says what the thing IS and is written once; a caveat says what is currently missing from
   * it — "12 scans have no resolved country, so they are not counted here" — and is computed
   * per render from the data on screen. Keeping them separate is what lets a `term` card keep
   * its glossary definition AND carry a caveat, instead of one overwriting the other.
   *
   * Passing this MARKS the affordance (`caveatMarkSx`), so the caveat is discoverable without
   * hover. The mark is deliberately not a colour that reads as an alert — see that helper.
   */
  caveat?: React.ReactNode
  /** Optional guide link for concepts too big for a definition. `category/slug[#heading]`. */
  doc?: string
  /**
   * Defaults to a small help glyph.
   *
   * One caveat when you pass your own: the glossary overlay returns focus to the DEFAULT
   * glyph via a ref, and a custom child is not cloned to carry that ref (cloning a child
   * that does not forward one would break it outright). So a custom trigger still opens
   * the overlay, but closing it leaves focus on the document rather than back on the
   * trigger. Pass the default glyph where the tooltip is a plain "what is this?".
   */
  children?: React.ReactElement
  placement?: 'top' | 'bottom' | 'left' | 'right'
}

/**
 * The house tooltip for explaining something.
 *
 * Two reasons it exists rather than another raw `<Tooltip>`:
 *
 * 1. There were 49 files hand-rolling MUI `Tooltip`, so "the tooltip" had no consistent
 *    body width, type scale, or footer treatment — and no way to add one later without
 *    touching 49 files (§36).
 * 2. It makes the glossary the path of least resistance. `term="activation-trigger"` is
 *    less typing than writing a sentence, so the correct thing is also the easy thing.
 *    A rule that costs the author extra keystrokes loses to one that saves them.
 *
 * The footer deep-links to the term's anchor, NOT to a search for the word (§44). The
 * reader is already confused; a result list asks them to pick the right answer to the
 * question they just failed to answer.
 *
 * That argument was right about the DESTINATION and incomplete about the TRIP. Reaching the
 * anchor still meant a full navigation: someone reading a billing page who wanted one more
 * sentence about "Total activation cost" landed in the docs reader and had to find their way
 * back. So the footer now opens the definition IN PLACE (`TermOverlay`) whenever there is
 * more to show than the tooltip already showed — and still navigates, exactly as before,
 * when there is not. The anchor remains the destination; it is just no longer the only way
 * to read the entry.
 */
export const InfoTooltip = ({ term, text, caveat, doc, children, placement = 'top', hideDefinitionLink = false }: Props) => {
  const theme = useTheme()
  // Resolved against the READER's corpus, so an `audience: internal` term explains itself
  // to staff and stays invisible to a brand. A brand hitting an internal id gets exactly
  // what it got before this was audience-aware: no entry, and the `text` fallback below.
  const entry = useGlossaryTerm(term)

  const [overlayOpen, setOverlayOpen] = useState(false)
  // The Tooltip is controlled ONLY so the overlay can force it shut. MUI gives tooltips
  // zIndex 1500 and modals 1300, so a tooltip left open by a keyboard activation floats
  // ABOVE the dialog it just opened. `onOpen`/`onClose` keep hover, focus, and both delays
  // behaving exactly as the uncontrolled version did.
  const [tipOpen, setTipOpen] = useState(false)
  const triggerRef = useRef<HTMLSpanElement>(null)

  // A `term` that does not resolve is an authoring mistake, not a runtime state: the id
  // is build-checked in the glossary but nothing checks the CALL SITE. Fall back to
  // whatever literal text was given so the tooltip degrades to useful rather than empty,
  // and complain loudly in dev so it gets fixed rather than shipped.
  //
  // There are now TWO reasons this can miss, and the message says both, because the second
  // is not a bug and a warning that only describes the first sends you looking for a typo
  // in a correct call site. An `audience: internal` term legitimately does not resolve for
  // a brand reader — that is the gate working, and the tooltip simply does not render.
  if (term && !entry && import.meta.env.DEV) {
    console.warn(
      `[InfoTooltip] term "${term}" did not resolve. Either it is not in the glossary — ` +
      `add it to docs/glossary.json and re-run publish-glossary.mjs, or use \`text\` if it ` +
      `is not a term of art — or it is an \`audience: internal\` entry and you are signed ` +
      `in as a brand user, in which case this is correct and nothing renders.`,
    )
  }

  const body = text ?? entry?.short
  // A caveat with no body is a legitimate tooltip — a stat tile whose `tooltip` is the bare
  // `true` (icon only) can still be carrying one. Returning the naked child there would drop
  // the caveat on the floor with nothing on screen saying so.
  if (!body && !caveat) return children ?? null

  const footerHref = hideDefinitionLink
    ? undefined
    : entry
      ? glossaryHref(entry.id)
      : doc
        ? `/docs/${doc}`
        : undefined
  const footerLabel = entry ? `Definition · ${entry.term}` : 'Read the guide'

  // Only when the entry has something the tooltip did not already show. `short` IS the
  // tooltip body (§44), so an overlay that repeats it is a click for nothing — see
  // `hasExpandedEntry`. When this is false the footer keeps its original behaviour and
  // navigates, which is the honest outcome rather than a dialog that disappoints.
  const opensOverlay = Boolean(entry && hasExpandedEntry(entry))

  const title = (
    <Stack spacing={2} sx={{ py: 1 }}>
      {body && (
        <Typography variant="body3" sx={{ fontSize: 'inherit', lineHeight: 1.6 }}>
          {body}
        </Typography>
      )}
      {caveat && (
        <>
          {/* Ruled off rather than run on. The body is a definition and the caveat is a
              statement about today's data; a reader has to be able to tell which sentence
              they are looking at, and a paragraph break alone does not carry that. */}
          {body && <Box sx={{ height: '1px', backgroundColor: theme.palette.divider }} />}
          {/*
            WRAPS. It is not clamped here and must not be — the card face clamps its own
            inline line to survive a fixed-height grid cell, and this tooltip is the place
            that sentence is finally readable in full. Truncating it in both places would
            leave the text with nowhere to be read.
          */}
          <Typography variant="body3" color="text.secondary" sx={{ fontSize: 'inherit', lineHeight: 1.5 }}>
            {caveat}
          </Typography>
        </>
      )}
      {footerHref && (
        <>
          <Box sx={{ height: '1px', backgroundColor: theme.palette.divider }} />
          {/*
            STILL a real link, and for the original reason: it must be middle-clickable and
            copyable like any other reference in the app. Opening the overlay does not change
            that — it intercepts the PLAIN left click and lets every other gesture through, so
            ⌘/ctrl-click, shift-click, middle-click and "copy link address" all continue to
            resolve the glossary anchor. Replacing the anchor with a `<button>` would have
            been the easy version and would have silently taken those away.

            (Middle click never reaches `onClick` in React — it fires `auxclick` — so it needs
            no special case here; the modifier check covers the rest.)
          */}
          <Typography
            component={RouterLink}
            to={footerHref}
            variant="body3"
            onClick={(e: React.MouseEvent) => {
              if (!opensOverlay) return
              if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return
              e.preventDefault()
              setTipOpen(false)
              // Focus the trigger BEFORE opening, so MUI's focus trap captures IT as the
              // element to restore to on close.
              //
              // The obvious version — restore focus by hand in onClose — does not work, and
              // fails silently: the dialog unmounts on a Fade (`closeAfterTransition`), so
              // MUI's own restore runs a few hundred ms LATER and overwrites whatever the
              // handler focused. Measured: focus ended on <body>. Racing it with a longer
              // timer would be guessing at a transition duration this component does not own.
              //
              // What MUI restores to is `document.activeElement` at trap activation, and that
              // is a thing this component CAN control. Left alone it would be the footer link
              // inside the tooltip bubble, which unmounts with the tooltip — hence <body>.
              triggerRef.current?.focus()
              setOverlayOpen(true)
            }}
            sx={{
              // See the header: the SIZE comes from the tooltip container, never the variant.
              fontSize: 'inherit',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              // See GlossaryPage — the brand[400] literal was the dark value applied in
              // both modes, failing AA at 2.45:1 in light. `brandText` splits them.
              color: brandText(theme),
              textDecoration: 'none',
              '&:hover': { color: brandTextHover(theme), textDecoration: 'underline' },
            }}
          >
            {footerLabel}
            {/* The glyph carries WHERE the click goes, which is the only thing that differs
                between the two paths — the label is accurate either way, since both end in
                the definition. An up-right arrow on something that opens in place would be a
                small lie, and it is the kind readers notice. */}
            {opensOverlay
              ? <BookOpenIcon size={12} strokeWidth={1.5} />
              : <ArrowUpRightIcon size={12} strokeWidth={1.5} />}
          </Typography>
        </>
      )}
    </Stack>
  )

  return (
    <>
    <Tooltip
      title={title}
      placement={placement}
      arrow
      // Forced shut while the dialog is up (see the zIndex note above); otherwise this is
      // ordinary hover/focus behaviour, just observed rather than left to MUI.
      open={tipOpen && !overlayOpen}
      onOpen={() => setTipOpen(true)}
      onClose={() => setTipOpen(false)}
      // The footer is a link, so the tooltip has to survive the pointer travelling from
      // the trigger into the bubble. Without this it closes on the way and the footer is
      // decorative.
      disableInteractive={false}
      enterDelay={200}
      leaveDelay={120}
      // A COARSE pointer has no hover, so without this the only way to read any tooltip in the
      // app is MUI's 700ms long-press — a gesture nothing on screen advertises. That was
      // tolerable while every trigger was a dedicated `(i)` glyph, and stopped being so once the
      // trigger could be a status chip (VRG-437): the chip carries no glyph at all, so on touch
      // the affordance would not exist rather than merely being slow to find.
      //
      // 0, not a shorter delay: the delay exists to disambiguate a press from a scroll, and this
      // component's triggers are small inline elements nobody scrolls by dragging.
      enterTouchDelay={0}
      // 4s, up from MUI's 1500. The body is a definition, and 1.5s is not long enough to read
      // one — on a phone the reader also cannot keep it open by hovering, which is the escape
      // hatch that makes the short default workable with a mouse.
      leaveTouchDelay={4000}
      slotProps={{
        tooltip: {
          sx: {
            maxWidth: 280,
            px: 3,
            py: 1,
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: theme.shadows[3],
          },
        },
        arrow: { sx: { color: theme.palette.background.paper } },
      }}
    >
      {children ?? (
        <Box
          component="span"
          ref={triggerRef}
          tabIndex={0}
          aria-label={entry ? `What is ${entry.term}?` : 'More information'}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            ml: 1,
            color: theme.palette.text.secondary,
            cursor: 'help',
            verticalAlign: 'middle',
            '&:hover': { color: theme.palette.text.primary },
            // Spread LAST so the mark's tone and hover win over the rest state above.
            ...(caveat ? caveatMarkSx(theme) : null),
          }}
        >
          <InfoGlyph />
        </Box>
      )}
    </Tooltip>

    {entry && opensOverlay && (
      <TermOverlay
        termId={entry.id}
        open={overlayOpen}
        onClose={() => setOverlayOpen(false)}
      />
    )}
    </>
  )
}
